/**
 * One-shot, once per machine: put the five pictures of the statements
 * (README "Fundamentals: the statements as pictures") at the bottom of the
 * Fundamental Analysis page's saved arrangements, which were seeded before
 * they existed (the owner, 2026-10-29: "add the five panes as new rows at the
 * bottom of my saved Fundamentals layout"). The page seed is idempotent by
 * name, so an arrangement that already exists never takes a new pane on its
 * own.
 *
 * Three rows go below whatever the arrangement holds, as a first narrow
 * arrangement draws them: Income Flow alone, then Revenue Mix beside Margins,
 * then Cash Flow beside Balance Sheet. A root that is already a stack of rows
 * keeps its id and gains the rows; any other root becomes the first row of a
 * new stack and keeps its own id, so the widths saved under it stand. A
 * row's saved height is kept by its place in its stack, so the rows above
 * keep theirs. On the narrow arrangement the new rows start at their panes'
 * own heights, as its rows grow with their panes. The wide one fills the
 * window, where a row nobody dragged starts at 380px, which cuts Income Flow
 * off (seen in a headless browser at the Samsung 49's 3840x1080), so its
 * Income Flow row is written at that pane's own height (`FLOW_ROW_PX`), in
 * each place a drag would have written it, and the other two start at 380px.
 * Below the wide arrangement's columns the page now scrolls, its columns at
 * the height they need (README principle 65).
 *
 * A tree that already holds any of the five is left alone, so a reader who
 * placed them, or removed them after this ran, keeps it so. The flag is not
 * an `mpp:` key: it is this machine's, because each machine holds its own
 * arrangements, and it is not synced.
 */
import { rawGet, rawSet } from "@/lib/display/layoutNamespace";
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import type { WsNode, WorkspacePage } from "@/lib/workspace/workspacePages";

const FLAG = "migrate:fundamentals-pictures:v1";
const LIST_KEY = "mpp:workspaces:v1";
const NARROW = "fundamental analysis · verdict";
const WIDE = "fundamental analysis · verdict · wide";
const PAGE_NAMES = new Set([NARROW, WIDE]);
/** The pane's panel: PanelWorkspacePage's `${storageKey}:ws`, as the nested workspace names its row heights. */
const PANEL_ID = "mpp:fundamentals-preview:pw:v1:ws";
const DP_SEP = "\u0000@dp:";
/** Income Flow's row on the wide arrangement: the pane's own `rowPx` (components/fundamentals/visuals/paneDefs.tsx). */
export const FLOW_ROW_PX = 460;

/** The five panes, in the rows they take: one, two, two. */
export const PICTURE_ROWS: ReadonlyArray<readonly string[]> = [["funvIncomeFlow"], ["funvRevenueMix", "funvMargins"], ["funvCashFlow", "funvBalanceSheet"]];
const PICTURES = new Set(PICTURE_ROWS.flat());

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function holdsAny(node: WsNode): boolean {
  if (node.t === "leaf") return node.component != null && PICTURES.has(node.component);
  return node.children.some(holdsAny);
}

/**
 * The tree with the pictures' rows at its bottom, the id of the stack that
 * holds them and Income Flow's place in it; null when the tree already holds
 * any of them. Pure: `ids` makes the new nodes' ids.
 */
export function withPicturesBelow(root: WsNode, ids: (prefix: string) => string = nid): { root: WsNode; stackId: string; flowRow: number } | null {
  if (holdsAny(root)) return null;
  const rows: WsNode[] = PICTURE_ROWS.map((row) =>
    row.length === 1
      ? { id: ids("l"), t: "leaf", component: row[0]! }
      : { id: ids("s"), t: "split", dir: "h", children: row.map((c) => ({ id: ids("l"), t: "leaf", component: c })), sizes: row.map(() => 100 / row.length) },
  );
  if (root.t === "split" && root.dir === "v") return { root: { ...root, children: [...root.children, ...rows] }, stackId: root.id, flowRow: root.children.length };
  const stackId = ids("s");
  return { root: { id: stackId, t: "split", dir: "v", children: [root, ...rows] }, stackId, flowRow: 1 };
}

/**
 * A stack's saved heights with Income Flow's row at `FLOW_ROW_PX`: the rows
 * above keep what they saved, and a row between with nothing saved stays so
 * (null, which reads as a row nobody dragged).
 */
export function heightsWithFlow(saved: string | null, flowRow: number): string {
  let arr: unknown[] = [];
  try {
    const parsed: unknown = saved ? JSON.parse(saved) : [];
    if (Array.isArray(parsed)) arr = parsed;
  } catch {
    arr = [];
  }
  const out = Array.from({ length: flowRow + 1 }, (_, i) => (i < arr.length && typeof arr[i] === "number" ? arr[i] : null));
  out[flowRow] = FLOW_ROW_PX;
  return JSON.stringify(out);
}

const rowKey = (pageId: string, stackId: string) => `mpp:ws:rowh:nested-ws:${PANEL_ID}:${pageId}:${stackId}:v1`;

export function migrateFundamentalsPictures(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(FLAG) === "1") return;
  } catch {
    return;
  }

  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }

  const pageIds = new Set<string>();
  const wideIds = new Set<string>();
  let poolChanged = false;
  const nextPool = pool.map((page) => {
    if (!page?.root || typeof page.name !== "string" || !PAGE_NAMES.has(page.name.trim().toLowerCase())) return page;
    pageIds.add(page.id);
    const wide = page.name.trim().toLowerCase() === WIDE;
    if (wide) wideIds.add(page.id);
    const next = withPicturesBelow(page.root);
    if (!next) return page;
    poolChanged = true;
    // The shared tree is what a profile with no tree of its own draws, with the shared row heights.
    if (wide) rawSet(rowKey(page.id, next.stackId), heightsWithFlow(rawGet(rowKey(page.id, next.stackId)), next.flowRow));
    return { ...page, root: next.root, updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  // Each Display Profile's own tree of those pages, and the shared override: each written where it was read.
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith("mpp:ws-tree:")) keys.push(key);
    }
    for (const key of keys) {
      const [logical, profile] = key.split(DP_SEP);
      const pageId = [...pageIds].find((id) => logical === `mpp:ws-tree:${id}:v1`);
      if (!pageId) continue;
      const raw = rawGet(key);
      if (!raw) continue;
      const root = JSON.parse(raw) as WsNode;
      if (!root || (root.t !== "leaf" && root.t !== "split")) continue;
      const next = withPicturesBelow(root);
      if (!next) continue;
      rawSet(key, JSON.stringify(next.root));
      if (wideIds.has(pageId)) {
        const heightsKey = `${rowKey(pageId, next.stackId)}${profile != null ? `${DP_SEP}${profile}` : ""}`;
        rawSet(heightsKey, heightsWithFlow(rawGet(heightsKey), next.flowRow));
      }
    }
  } catch {
    /* the shared tree's change still stands */
  }

  try {
    window.localStorage.setItem(FLAG, "1");
  } catch {
    /* quota: it runs again next time, and finds nothing to do */
  }
}
